/**
 * Route-of-Administration Backfill · Data Ops · Jul 2026
 *
 * Two-stage fill for the mostly-empty `route_of_administration` field:
 *   Stage A — deterministic keyword extraction (FREE, instant)
 *   Stage B — Claude Sonnet 4.5 inference (only where A finds nothing)
 *
 * Route: /data-ops/roa-backfill
 * Endpoints: /api/data-ops/roa-backfill/{preview,start,status,cancel,results,promote,scopes}
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Syringe, PlayCircle, StopCircle, CheckCircle2, AlertCircle, Database, ArrowUpCircle, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 3000;

const CONFIDENCE_TABS = [
  { key: 'high', label: 'High · auto-promoted', tone: 'emerald' },
  { key: 'medium', label: 'Medium · auto-promoted', tone: 'amber' },
  { key: 'low', label: 'Low · manual', tone: 'rose' },
  { key: 'null', label: 'Unclassified', tone: 'slate' },
];

const TONE_CLS = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  amber:   'bg-amber-50 border-amber-200 text-amber-800',
  rose:    'bg-rose-50 border-rose-200 text-rose-800',
  slate:   'bg-slate-50 border-slate-200 text-slate-700',
  teal:    'bg-teal-50 border-teal-200 text-teal-800',
};

export default function RoaBackfillPage() {
  const [scope, setScope] = useState('global');
  const [scopes, setScopes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [results, setResults] = useState({});
  const [activeTab, setActiveTab] = useState('high');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await axios.get(`${API}/api/data-ops/roa-backfill/scopes`, { withCredentials: true });
        setScopes(s.data.items || []);
      } catch { /* no-op */ }
      try {
        const st = await axios.get(`${API}/api/data-ops/roa-backfill/status`, { withCredentials: true });
        setJob(st.data.job || null);
      } catch { /* no-op */ }
    })();
  }, []);

  useEffect(() => {
    let alive = true;
    setPreviewLoading(true);
    axios
      .get(`${API}/api/data-ops/roa-backfill/preview?scope=${scope}`, { withCredentials: true })
      .then((r) => { if (alive) setPreview(r.data); })
      .catch(() => { if (alive) setPreview(null); })
      .finally(() => { if (alive) setPreviewLoading(false); });
    return () => { alive = false; };
  }, [scope]);

  useEffect(() => {
    if (!job || !['queued', 'running'].includes(job.status)) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/data-ops/roa-backfill/status?job_id=${job.job_id}`, { withCredentials: true });
        if (r.data?.job) setJob(r.data.job);
      } catch { /* no-op */ }
    }, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [job?.job_id, job?.status]); // eslint-disable-line

  useEffect(() => {
    axios
      .get(`${API}/api/data-ops/roa-backfill/results?confidence=${activeTab}&limit=50`, { withCredentials: true })
      .then((r) => setResults(prev => ({ ...prev, [activeTab]: r.data.items || [] })))
      .catch(() => {});
  }, [activeTab, job?.status]);

  const doStart = async () => {
    setStarting(true);
    try {
      const r = await axios.post(`${API}/api/data-ops/roa-backfill/start`, { scope }, { withCredentials: true });
      setJob(r.data.job);
      setConfirmOpen(false);
      toast.success(`Backfill started · ${preview?.total || 0} assets queued`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start');
    } finally { setStarting(false); }
  };

  const doCancel = async () => {
    if (!job) return;
    try {
      await axios.post(`${API}/api/data-ops/roa-backfill/cancel`, { job_id: job.job_id }, { withCredentials: true });
      toast.info('Backfill cancelled');
      const st = await axios.get(`${API}/api/data-ops/roa-backfill/status?job_id=${job.job_id}`, { withCredentials: true });
      setJob(st.data.job || null);
    } catch { toast.error('Cancel failed'); }
  };

  const [pendingMedium, setPendingMedium] = useState(0);
  useEffect(() => {
    axios.get(`${API}/api/data-ops/roa-backfill/pending-promotion`, { withCredentials: true })
      .then((r) => setPendingMedium(r.data.count || 0))
      .catch(() => {});
  }, [job?.status]);

  const doPromote = async () => {
    try {
      const r = await axios.post(`${API}/api/data-ops/roa-backfill/promote`, {}, { withCredentials: true });
      toast.success(`Promoted ${r.data.promoted} medium-confidence rows to primary RoA`);
      setPendingMedium(0);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Promotion failed'); }
  };

  const progressPct = useMemo(() => {
    if (!job || !job.total) return 0;
    return Math.round(((job.processed || 0) / job.total) * 100);
  }, [job]);

  const running = job && ['queued', 'running'].includes(job.status);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="roa-backfill-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Route of Administration Backfill</h1>
          <p className="text-sm text-slate-600 mt-0.5 max-w-2xl">
            Fills the blank <code className="text-[11px] bg-slate-100 rounded px-1">route_of_administration</code> field the exports read. First tries a <strong>free keyword pass</strong> over each asset&apos;s description; anything it can&apos;t resolve falls back to <strong>Claude Sonnet 4.5</strong> inference from the modality + indication.
          </p>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-1">
          <Syringe className="w-3 h-3" /> AI Data Ops
        </span>
      </div>

      {/* Scope + Preview */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="roa-backfill-scope-card">
        <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Scope</div>
        <div className="flex flex-wrap gap-2">
          {scopes.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setScope(s.key)}
              className={`text-[12px] px-3 py-1.5 rounded-full border transition ${
                scope === s.key
                  ? 'bg-teal-600 border-teal-600 text-white'
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
              data-testid={`roa-backfill-scope-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="pt-3 border-t border-slate-100 flex items-start gap-4 flex-wrap">
          <div className="min-w-[150px]">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Assets to fill</div>
            <div className="text-3xl font-black tabular-nums text-slate-900" data-testid="roa-backfill-preview-total">
              {previewLoading ? <Loader2 className="w-6 h-6 animate-spin text-slate-400" /> : (preview?.total || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">Blank RoA + some description.</div>
          </div>
          <div className="min-w-[150px]">
            <div className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold flex items-center gap-1">
              <Zap className="w-3 h-3" /> Free (keyword)
            </div>
            <div className="text-3xl font-black tabular-nums text-emerald-700" data-testid="roa-backfill-det-estimate">
              {previewLoading ? '—' : (preview?.deterministic_estimate || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">Resolved instantly, $0.</div>
          </div>
          <div className="min-w-[150px]">
            <div className="text-[10px] uppercase tracking-wide text-teal-600 font-semibold">AI (Claude)</div>
            <div className="text-3xl font-black tabular-nums text-teal-700" data-testid="roa-backfill-ai-estimate">
              {previewLoading ? '—' : (preview?.ai_estimate || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">Inferred where no keyword.</div>
          </div>
          <div className="flex flex-col gap-2 ml-auto">
            {!running ? (
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={!preview?.total || starting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="roa-backfill-start"
              >
                <PlayCircle className="w-4 h-4 mr-1" /> Start Backfill
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={doCancel}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="roa-backfill-cancel"
              >
                <StopCircle className="w-4 h-4 mr-1" /> Cancel Job
              </Button>
            )}
            <Button
              variant="outline"
              onClick={doPromote}
              disabled={running || pendingMedium === 0}
              data-testid="roa-backfill-promote"
              className={pendingMedium > 0 ? 'border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100' : ''}
              title="Promote all medium-confidence AI-inferred routes to the primary field."
            >
              <ArrowUpCircle className="w-4 h-4 mr-1" /> Promote Medium{pendingMedium > 0 ? ` (${pendingMedium})` : ''} →
            </Button>
          </div>
        </div>

        <div className="border border-slate-100 rounded-md divide-y divide-slate-100 max-h-40 overflow-y-auto">
          {(preview?.sample || []).map((s) => (
            <div key={s.id} className="px-2.5 py-1 text-[11px] flex items-start gap-2">
              <span className="font-mono text-slate-500 shrink-0">{s.asset_name || '—'}</span>
              <span className="text-slate-400 shrink-0">·</span>
              <span className="text-slate-400 truncate flex-1">{s.indication}</span>
              {s.deterministic_guess && (
                <span className="shrink-0 text-[10px] rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-1.5">
                  <Zap className="w-2.5 h-2.5 inline" /> {s.deterministic_guess}
                </span>
              )}
            </div>
          ))}
          {!preview?.sample?.length && !previewLoading && (
            <div className="text-[11px] text-slate-400 italic px-2.5 py-2">No blank-RoA assets in this scope.</div>
          )}
        </div>
      </div>

      {/* Job progress card */}
      {job && (
        <div className={`rounded-lg border p-4 ${
          running ? 'border-teal-300 bg-teal-50/40'
          : job.status === 'done' ? 'border-emerald-300 bg-emerald-50/40'
          : job.status === 'error' ? 'border-rose-300 bg-rose-50/40'
          : 'border-slate-200 bg-slate-50/60'
        }`} data-testid="roa-backfill-job-card">
          <div className="flex items-center gap-2 flex-wrap">
            {running && <Loader2 className="w-4 h-4 animate-spin text-teal-600" />}
            {job.status === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
            {job.status === 'error' && <AlertCircle className="w-4 h-4 text-rose-600" />}
            <span className="text-sm font-semibold text-slate-800">
              Job <span className="font-mono text-[11px]">{job.job_id}</span> · {job.status}
            </span>
            <span className="text-[11px] text-slate-500">scope · {job.scope}</span>
            {job.current_asset && running && (
              <span className="text-[11px] text-slate-500">now: {job.current_asset}</span>
            )}
          </div>
          {job.total > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1">
                <span data-testid="roa-backfill-progress-text">{(job.processed || 0).toLocaleString()} / {job.total.toLocaleString()} · {progressPct}%</span>
                <div className="flex flex-wrap gap-2 text-[10px]">
                  {Object.entries(job.counts || {}).map(([k, v]) => (
                    <span key={k} className="rounded border border-slate-200 bg-white px-1.5 py-0.5">
                      {k.replace(/_/g, ' ')}: <strong className="tabular-nums">{v}</strong>
                    </span>
                  ))}
                </div>
              </div>
              <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className={`h-full transition-all ${running ? 'bg-teal-500' : job.status === 'done' ? 'bg-emerald-500' : 'bg-slate-400'}`}
                  style={{ width: `${progressPct}%` }}
                  data-testid="roa-backfill-progress-bar"
                />
              </div>
            </div>
          )}
          {job.error && (<div className="mt-2 text-[11px] text-rose-700">{job.error}</div>)}
        </div>
      )}

      {/* Results tabs */}
      <div className="rounded-lg border border-slate-200 bg-white" data-testid="roa-backfill-results">
        <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-100 overflow-x-auto">
          {CONFIDENCE_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`text-[11px] font-medium px-2.5 py-1 rounded border transition whitespace-nowrap ${
                activeTab === t.key ? TONE_CLS[t.tone] : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
              data-testid={`roa-backfill-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto max-h-[500px]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Asset</th>
                <th className="text-left px-3 py-2 font-medium">Company</th>
                <th className="text-left px-3 py-2 font-medium">Description</th>
                <th className="text-left px-3 py-2 font-medium">Inferred RoA</th>
                <th className="text-left px-3 py-2 font-medium">Confidence</th>
                <th className="text-left px-3 py-2 font-medium">Rationale</th>
                <th className="text-left px-3 py-2 font-medium">Promoted?</th>
              </tr>
            </thead>
            <tbody>
              {(results[activeTab] || []).map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800 truncate max-w-[160px]">{r.asset_name || '—'}</td>
                  <td className="px-3 py-2 text-slate-600 truncate max-w-[140px]">{r.company_name || '—'}</td>
                  <td className="px-3 py-2 text-slate-500 truncate max-w-[240px]" title={r.indication}>{r.indication}</td>
                  <td className="px-3 py-2 text-slate-700 font-medium truncate max-w-[140px]">
                    {r.route_of_administration_ai_inferred || <span className="italic text-slate-400">null</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] rounded-full border px-1.5 py-0.5 ${
                      r.route_of_administration_ai_confidence === 'high' ? TONE_CLS.emerald
                      : r.route_of_administration_ai_confidence === 'medium' ? TONE_CLS.amber
                      : TONE_CLS.rose
                    }`}>{r.route_of_administration_ai_confidence || '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-[11px] truncate max-w-[220px]" title={r.route_of_administration_ai_rationale}>{r.route_of_administration_ai_rationale}</td>
                  <td className="px-3 py-2 text-[10px]">
                    {r.route_of_administration && r.route_of_administration_source ? (
                      <span className="text-emerald-700"><CheckCircle2 className="w-3 h-3 inline" /> {r.route_of_administration_source}</span>
                    ) : (
                      <span className="text-slate-400 italic">no</span>
                    )}
                  </td>
                </tr>
              ))}
              {!results[activeTab]?.length && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-400 text-sm italic">
                    <Database className="w-5 h-5 inline mr-1" /> No rows filled at this confidence yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Start-confirm dialog */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          onClick={() => setConfirmOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}
            data-testid="roa-backfill-confirm-modal">
            <div className="text-lg font-bold text-slate-900 mb-1">Start Backfill?</div>
            <p className="text-[13px] text-slate-600 mb-3">
              <strong>{(preview?.deterministic_estimate || 0).toLocaleString()}</strong> assets resolve instantly via keyword match (free). The remaining <strong>{(preview?.ai_estimate || 0).toLocaleString()}</strong> are sent to Claude Sonnet 4.5. High-confidence + keyword hits auto-promote to <code>route_of_administration</code>.
            </p>
            <ul className="text-[11px] text-slate-500 space-y-0.5 mb-4">
              <li>· Scope: {scopes.find(s => s.key === scope)?.label}</li>
              <li>· Model: claude-sonnet-4-5-20250929 · via Emergent LLM Key</li>
              <li>· Cost estimate: ~${(((preview?.ai_estimate || 0)) * 0.002).toFixed(2)} (only the AI rows cost tokens)</li>
              <li>· You can cancel mid-run; already-filled rows persist.</li>
            </ul>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="roa-backfill-confirm-cancel">Cancel</Button>
              <Button
                onClick={doStart}
                disabled={starting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="roa-backfill-confirm-go"
              >
                {starting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
                Start
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
